import { useState, useEffect } from "react";
import axios from "axios";
import { Hourglass } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";

const STATUS_STYLE = {
  "Due for Release": "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  "Due in 30 Days": "bg-amber-100 text-amber-700 hover:bg-amber-100",
  "Held": "bg-slate-100 text-slate-600 hover:bg-slate-100",
  "No Completion Date": "bg-red-100 text-red-700 hover:bg-red-100",
};
const CARD_META = [
  { key: "Due for Release", tone: "text-emerald-600", note: "Defect liability period over — release now" },
  { key: "Due in 30 Days", tone: "text-amber-600", note: "Release window approaching" },
  { key: "Held", tone: "text-slate-500", note: "Still within defect liability period" },
];

export default function RetentionTracker() {
  const [data, setData] = useState(null);

  const load = () => axios.get(`${API}/reports/retention-tracker`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener("sap-synced", load);
    return () => window.removeEventListener("sap-synced", load);
  }, []);

  if (!data) return <div className="animate-pulse text-slate-400 text-sm">Loading retention tracker...</div>;

  return (
    <div className="space-y-6" data-testid="retention-tracker-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Retention Release Tracker</h1>
        <p className="text-sm text-slate-500 mt-1">Retention amounts (RT / Special GL H) mapped to project completion + 180-day defect liability period</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {CARD_META.map(({ key, tone, note }) => {
          const s = data.summary[key] || { total: 0, count: 0 };
          return (
            <Card key={key} className="border-slate-200 shadow-sm fade-in-up" data-testid={`retention-card-${key.toLowerCase().replace(/\s+/g, "-")}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 mb-2">{key}</p>
                    <p className="text-2xl font-black tracking-tighter text-slate-900">{inrShort(s.total)}</p>
                    <p className={`text-xs mt-1.5 font-medium ${tone}`}>{s.count} items · {note}</p>
                  </div>
                  <div className="h-9 w-9 rounded-md bg-sky-50 flex items-center justify-center">
                    <Hourglass className="h-4 w-4 text-sky-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardContent className="p-4 overflow-x-auto">
          <Table data-testid="retention-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Side", "Invoice", "Party", "WBS / Project", "Completion", "Release Due", "Days", "Amount", "Status"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((it, i) => (
                <TableRow key={i} className="border-b border-slate-200 hover:bg-slate-50/50">
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${it.side === "AP" ? "border-sky-300 text-sky-700" : "border-amber-300 text-amber-700"}`}>{it.side}</Badge>
                  </TableCell>
                  <TableCell className="text-sm font-mono text-slate-700">{it.invoice_no}</TableCell>
                  <TableCell className="text-sm text-slate-700 whitespace-nowrap">{it.party}</TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                    <span className="font-medium">{it.wbs_element}</span>
                    <span className="block text-xs text-slate-400">{it.project_name}</span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{it.completion_date || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{it.release_due_date || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600 text-right">
                    {it.days_to_release == null ? "—" : it.days_to_release <= 0 ? `${-it.days_to_release}d overdue` : `in ${it.days_to_release}d`}
                  </TableCell>
                  <TableCell className="text-sm font-semibold text-slate-900 text-right">{inr(it.amount)}</TableCell>
                  <TableCell>
                    <Badge className={STATUS_STYLE[it.status]}>{it.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data.items.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No retention items found. Sync SAP data to refresh.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
